import React, { useState } from 'react';
import { Tabs, Tab } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useMutation } from '@apollo/client';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import EnhancedButton from '@components/button/EnhancedButton';
import TabPanel from '@components/layout/tabs/TabPanel';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import { modifyBankersDetails } from './queries';
import { IPolicyBankersDetails } from '../../../..';
import { MAIN_THEME } from '@styles';

interface IBankersBlanketPopUpFormProps {
  currencySymbol: string;
  data: IPolicyBankersDetails[];
  lovs: Record<string, Record<string, string>>;
  onClose: () => void;
  onSuccess: () => void;
}

const useStyles = makeStyles()(() => ({
  content: {
    padding: '0 63px 40px 30px',
  },
  tabsRoot: {
    minHeight: 'auto',
    borderBottom: `1px solid ${MAIN_THEME.color.white92}`,
    marginBottom: '20px',
  },
  tabRoot: {
    textTransform: 'none',
    minWidth: 72,
    minHeight: 'auto',
    padding: '6px 12px',
    marginRight: '4px',
    color: MAIN_THEME.color.black47,
    fontFamily: MAIN_THEME.font.regular,
    '&.Mui-selected': {
      color: MAIN_THEME.color.black,
      fontFamily: MAIN_THEME.font.medium,
      backgroundColor: MAIN_THEME.color.white98,
    },
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  buttonsContainer: {
    marginTop: '2rem',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '15px',
  },
}));

const BankersBlanketPopUpForm: React.FC<IBankersBlanketPopUpFormProps> = ({
  data,
  onClose,
  onSuccess,
}) => {
  const { classes } = useStyles();
  const params = useParams();

  const [modifyBankersDetailsAction] = useMutation(modifyBankersDetails());

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [propertyDescriptions, setPropertyDescriptions] =
    useState<IPolicyBankersDetails[]>(data);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleFieldUpdate = (
    index: number,
    field: keyof IPolicyBankersDetails,
    value: string | number
  ) => {
    const newList = [...propertyDescriptions];
    newList[index][field] = value as never;
    setPropertyDescriptions(newList);
  };

  const handleSubmit = async () => {
    const policyBankersBlanketBondInput = propertyDescriptions.map(
      (desc, idx) => ({
        branchName: desc.branchName || '',
        isMain: desc.isMain.toUpperCase() === 'YES' ? true : false,
        isAgency: desc.isAgency.toUpperCase() === 'YES' ? true : false,
        numberOfEmployees: Number(desc.numberOfEmployees) || 0,
        underwritingYear: Number(desc.underwritingYear) || 0,
        infidelity: Number(desc.infidelity) || 0,
        burglary: Number(desc.burglary) || 0,
        holdUp: Number(desc.holdUp) || 0,
        inSafe: Number(desc.inSafe) || 0,
        inTransit: Number(desc.inTransit) || 0,
        forgedCheckes: Number(desc.forgedChecks) || 0,
        counterFeitCurrencies: Number(desc.counterfeitCurrencies) || 0,
        clause6: Number(desc.clause6) || 0,
        falsification: Number(desc.falsification) || 0,
        order: idx + 1,
      })
    );

    try {
      await modifyBankersDetailsAction({
        variables: {
          entityId: params.id,
          policyBankersBlanketBondInput,
        },
      });
      toast.success(
        <ToastSuccessMessage>
          Bankers Blanket Bond updated successfully!
        </ToastSuccessMessage>
      );
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(<ToastErrorMessage>{getError(err)}</ToastErrorMessage>);
    }
  };

  const activeDescription = propertyDescriptions[selectedTab];

  return (
    <div className={classes.content}>
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="BBB Tabs"
        classes={{ root: classes.tabsRoot }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        {propertyDescriptions.map((_, i) => (
          <Tab
            key={`branch-tab-${i}`}
            label={`Branch ${i + 1}`}
            classes={{ root: classes.tabRoot }}
          />
        ))}
      </Tabs>

      <TabPanel key={selectedTab} index={selectedTab}>
        <div className={classes.gridContainer}>
          <TextInputFormField
            title="Branch Name*"
            name="branchName"
            value={activeDescription.branchName || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'branchName', e.target.value)
            }
          />
          <SelectFormField
            title="Main*"
            name="isMain"
            value={activeDescription?.isMain?.toUpperCase() || ''}
            selectOptions={{ YES: 'Yes', NO: 'No' }}
            onChange={(val) => handleFieldUpdate(selectedTab, 'isMain', val)}
          />
          <SelectFormField
            title="Agency*"
            name="isAgency"
            value={activeDescription?.isAgency?.toUpperCase() || ''}
            selectOptions={{ YES: 'Yes', NO: 'No' }}
            onChange={(val) => handleFieldUpdate(selectedTab, 'isAgency', val)}
          />
          <NumberInputFormField
            title="Number of Employees*"
            name="numberOfEmployees"
            value={activeDescription.numberOfEmployees || ''}
            onChange={(e) =>
              handleFieldUpdate(
                selectedTab,
                'numberOfEmployees',
                parseInt(e.target.value) || 0
              )
            }
          />
          <NumberInputFormField
            title="Underwriting Year*"
            name="underwritingYear"
            value={activeDescription.underwritingYear.toString() || ''}
            onChange={(e) =>
              handleFieldUpdate(
                selectedTab,
                'underwritingYear',
                parseInt(e.target.value) || 0
              )
            }
          />
          <NumberInputFormField
            title="Infidelity*"
            name="infidelity"
            value={activeDescription.infidelity || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'infidelity', e.target.value)
            }
            maxDecimalPrecision={3}
          />
          <NumberInputFormField
            title="Burglary*"
            name="burglary"
            value={activeDescription.burglary || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'burglary', e.target.value)
            }
            maxDecimalPrecision={3}
          />
          <NumberInputFormField
            title="Hold Up*"
            name="holdUp"
            value={activeDescription.holdUp || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'holdUp', e.target.value)
            }
            maxDecimalPrecision={3}
          />
          <NumberInputFormField
            title="In Safe*"
            name="inSafe"
            value={activeDescription.inSafe || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'inSafe', e.target.value)
            }
            maxDecimalPrecision={3}
          />
          <NumberInputFormField
            title="In Transit*"
            name="inTransit"
            value={activeDescription.inTransit || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'inTransit', e.target.value)
            }
            maxDecimalPrecision={3}
          />
          <NumberInputFormField
            title="Forged Checks*"
            name="forgedChecks"
            value={activeDescription.forgedChecks || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'forgedChecks', e.target.value)
            }
            maxDecimalPrecision={3}
          />
          <NumberInputFormField
            title="Counterfeit Currencies*"
            name="counterfeitCurrencies"
            value={activeDescription.counterfeitCurrencies || ''}
            onChange={(e) =>
              handleFieldUpdate(
                selectedTab,
                'counterfeitCurrencies',
                e.target.value
              )
            }
            maxDecimalPrecision={3}
          />
          <NumberInputFormField
            title="Clause 6*"
            name="clause6"
            value={activeDescription.clause6 || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'clause6', e.target.value)
            }
            maxDecimalPrecision={3}
          />
          <NumberInputFormField
            title="Falsification*"
            name="falsification"
            value={activeDescription.falsification || ''}
            onChange={(e) =>
              handleFieldUpdate(selectedTab, 'falsification', e.target.value)
            }
            maxDecimalPrecision={3}
          />
        </div>
      </TabPanel>

      <div className={classes.buttonsContainer}>
        <EnhancedButton onClick={onClose}>Cancel</EnhancedButton>
        <EnhancedButton isPrimary onClick={handleSubmit}>
          Submit
        </EnhancedButton>
      </div>
    </div>
  );
};

export default BankersBlanketPopUpForm;
